import React, { useEffect, useState } from "react";
import { cloudinaryUrl } from "../constants/CloudinaryConstants";
import { allUsers } from "../actions/allUsers";
import { useNavigate } from "react-router-dom";
import { filterUsers } from "../actions/filterUsers";
import { AiOutlineUnorderedList, AiOutlineBars } from "react-icons/ai";
import ProfessionalCard from "../components/ProfessionalCard";
import SortDropdown from "../components/SortDropdown";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const Professionals = () => {
  const [all, setAll] = useState(null);
  const [filter, setFilter] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [sortBy, setSortBy] = useState("name-asc");
  const [searchName, setSearchName] = useState(""); // NEW: Search by name
  const [isLoading, setIsLoading] = useState(false); // Track if fetching fresh data
  const nav = useNavigate();

  const placeholderAvatar = "https://via.placeholder.com/64?text=No+Photo";

  const getDisplayName = (professional) =>
    `${professional?.legal_first_name || ""} ${
      professional?.legal_last_name || ""
    }`
      .trim()
      .replace(/\s+/g, " ") || "Name unavailable";

  const getCompany = (professional) =>
    professional?.brokerage_name || "Company unavailable";

  const getClassType = (professional) =>
    professional?.class_type || "Type unavailable";

  const getPhoto = (professional) =>
    professional?.picture_url
      ? cloudinaryUrl(professional.picture_url)
      : placeholderAvatar;

  useEffect(() => {
    setIsLoading(true);
    allUsers(setAll).finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (filter === null) {
      allUsers(setAll).finally(() => setIsLoading(false));
    } else {
      filterUsers(filter, setAll).finally(() => setIsLoading(false));
    }
  }, [filter]);

  const handleClearFilters = () => {
    setFilter(null);
  };

  const getSortedProfessionals = (professionals) => {
    if (!professionals) return professionals;

    // Filter out consumers and admin user_id 3
    const sorted = professionals.filter(
      (p) => p.class_type !== "Consumer" && p.user_id !== 3,
    );

    // Apply search filter first
    let filtered = sorted;
    if (searchName.trim()) {
      const search = searchName.toLowerCase().trim();
      filtered = sorted.filter((p) => {
        const fullName =
          `${p.legal_first_name || ""} ${p.legal_last_name || ""}`.toLowerCase();
        return fullName.includes(search);
      });
    }

    // Then apply sort
    switch (sortBy) {
      case "name-asc":
        return filtered.sort((a, b) =>
          `${a.legal_first_name} ${a.legal_last_name}`.localeCompare(
            `${b.legal_first_name} ${b.legal_last_name}`,
          ),
        );
      case "name-desc":
        return filtered.sort((a, b) =>
          `${b.legal_first_name} ${b.legal_last_name}`.localeCompare(
            `${a.legal_first_name} ${a.legal_last_name}`,
          ),
        );
      case "type":
        return filtered.sort((a, b) =>
          (a.class_type || "").localeCompare(b.class_type || ""),
        );
      case "company":
        return filtered.sort((a, b) =>
          (a.brokerage_name || "").localeCompare(b.brokerage_name || ""),
        );
      case "recent":
        return filtered.reverse();
      default:
        return filtered;
    }
  };

  if (!all) {
    return (
      <div className="mt-8 px-8">
        <Skeleton count={5} height={40} style={{ marginBottom: "20px" }} />
      </div>
    );
  }

  return (
    <div className="mt-8 px-8 pb-16">
      {/* Header Section */}
      <div className="mb-8 flex flex-col gap-4 py-4 px-6 shadow-md bg-slate-100">
        <div className="flex justify-between items-center">
          <h1 className="text-30">
            <b className="underline">Real Estate Professionals</b>
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("cards")}
              className={`px-3 py-2 rounded flex items-center gap-2 transition-all ${
                viewMode === "cards"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-300 text-gray-700"
              }`}
              title="Card View"
            >
              <AiOutlineUnorderedList size={20} />
              <span className="text-sm font-medium">Card View</span>
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-2 rounded flex items-center gap-2 transition-all ${
                viewMode === "table"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-300 text-gray-700"
              }`}
              title="Detailed View"
            >
              <AiOutlineBars size={20} />
              <span className="text-sm font-medium">Detailed View</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 items-center pb-4">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
          />
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-3 items-end">
          <button
            onClick={() => setFilter(null)}
            className={`h-[42px] px-5 py-2.5 rounded-lg font-semibold transition-all ${
              filter === null
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-slate-300 text-gray-700 hover:bg-slate-400"
            }`}
          >
            All Professionals
          </button>

          <button
            onClick={() => setFilter("Realtor")}
            className={`h-[42px] px-4 py-2 rounded font-semibold text-sm transition-all ${
              filter === "Realtor"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-slate-300 text-gray-700 hover:bg-slate-400"
            }`}
          >
            Realtors
          </button>
          <button
            onClick={() => setFilter("Lender")}
            className={`h-[42px] px-4 py-2 rounded font-semibold text-sm transition-all ${
              filter === "Lender"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-slate-300 text-gray-700 hover:bg-slate-400"
            }`}
          >
            Lenders
          </button>
          <button
            onClick={() => setFilter("Title Company")}
            className={`h-[42px] px-4 py-2 rounded font-semibold text-sm transition-all ${
              filter === "Title Company"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-slate-300 text-gray-700 hover:bg-slate-400"
            }`}
          >
            Title Companies
          </button>
          <button
            onClick={() => setFilter("Builder")}
            className={`h-[42px] px-4 py-2 rounded font-semibold text-sm transition-all ${
              filter === "Builder"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-slate-300 text-gray-700 hover:bg-slate-400"
            }`}
          >
            Builders
          </button>
          <button
            onClick={() => setFilter("Broker")}
            className={`h-[42px] px-4 py-2 rounded font-semibold text-sm transition-all ${
              filter === "Broker"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-slate-300 text-gray-700 hover:bg-slate-400"
            }`}
          >
            Brokers
          </button>

          {filter && (
            <button
              onClick={handleClearFilters}
              className="h-[42px] px-3 py-2 bg-gray-800 text-white rounded hover:bg-black transition-all font-medium text-sm"
            >
              Clear Filters
            </button>
          )}

          <div className="h-[42px] flex items-end ml-auto">
            <SortDropdown
              label="Sort by"
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: "name-asc", label: "Name (A - Z)" },
                { value: "name-desc", label: "Name (Z - A)" },
                { value: "type", label: "Professional Type" },
                { value: "company", label: "Company Name" },
                { value: "recent", label: "Recently Added" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && all && (
        <div className="mb-4 px-4 py-2 bg-blue-50 border-l-4 border-blue-500 text-blue-700 flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          <span className="text-sm font-medium">
            Refreshing professionals list...
          </span>
        </div>
      )}

      {/* Card View */}
      {viewMode === "cards" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {getSortedProfessionals(all)?.map((professional) => (
            <ProfessionalCard
              key={professional.user_id}
              professional={professional}
            />
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <div className="overflow-x-auto rounded-lg shadow-lg">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-200 border-b-2 border-gray-300">
                <th className="px-4 py-3 text-left font-bold text-gray-900 text-sm">
                  Photo
                </th>
                <th className="px-4 py-3 text-left font-bold text-gray-900 text-sm">
                  Name
                </th>
                <th className="px-4 py-3 text-left font-bold text-gray-900 text-sm">
                  Company
                </th>
                <th className="px-4 py-3 text-left font-bold text-gray-900 text-sm">
                  Professional Type
                </th>
                <th className="px-4 py-3 text-left font-bold text-gray-900 text-sm">
                  Phone
                </th>
                <th className="px-4 py-3 text-left font-bold text-gray-900 text-sm">
                  Email
                </th>
                <th className="px-4 py-3 text-center font-bold text-gray-900 text-sm">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedProfessionals(all)?.map((professional, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-300 hover:bg-gray-100 transition-colors group cursor-pointer"
                >
                  <td className="px-4 py-3 border-r border-gray-300">
                    <img
                      className="w-12 h-12 rounded-full object-cover"
                      src={getPhoto(professional)}
                      alt={getDisplayName(professional)}
                    />
                  </td>
                  <td className="px-4 py-3 border-r border-gray-300 font-medium text-gray-900">
                    {getDisplayName(professional)}
                  </td>
                  <td className="px-4 py-3 border-r border-gray-300 text-gray-700">
                    {getCompany(professional)}
                  </td>
                  <td className="px-4 py-3 border-r border-gray-300">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${
                        professional.class_type === "Realtor"
                          ? "bg-blue-500"
                          : professional.class_type === "Lender"
                            ? "bg-purple-500"
                            : professional.class_type === "Title Company"
                              ? "bg-green-500"
                              : professional.class_type === "Builder"
                                ? "bg-orange-500"
                                : professional.class_type === "Broker"
                                  ? "bg-red-500"
                                  : "bg-gray-500"
                      }`}
                    >
                      {getClassType(professional)}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-r border-gray-300 text-sm">
                    {professional?.phone_number ? (
                      <a
                        href={`tel:${String(professional.phone_number).replace(
                          /[^+\d]/g,
                          "",
                        )}`}
                        className="text-slate-800 hover:underline"
                      >
                        {professional.phone_number}
                      </a>
                    ) : (
                      <span className="text-gray-500">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-3 border-r border-gray-300 text-sm">
                    {professional?.email_address ? (
                      <a
                        href={`mailto:${professional.email_address}`}
                        className="text-slate-800 hover:underline"
                      >
                        {professional.email_address}
                      </a>
                    ) : (
                      <span className="text-gray-500">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() =>
                        professional?.user_id &&
                        nav(`/user/${professional.user_id}`)
                      }
                      className="bg-blue-500 text-white px-3 py-1 rounded text-sm font-semibold hover:bg-blue-600 transition-colors"
                    >
                      View Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {all?.length === 0 && (
        <div className="text-center py-16">
          <p className="text-gray-500 text-lg">No professionals found</p>
        </div>
      )}
    </div>
  );
};

export default Professionals;
